/**
 * Menu lateral numa fonte só: o aside desenha a árvore (seções e grupos), o
 * header deriva da lista plana a seção do breadcrumb e a paleta "Ir para…"
 * lista as mesmas páginas, com os mesmos nomes curtos. Quem não vê um item no
 * menu também não o vê na paleta. A exceção é o item `asideHidden`, que só a
 * paleta e o breadcrumb conhecem (`asideMenuTree` o tira do desenho do aside).
 */
import {useMemo} from 'react';

import {useAuth} from 'app/modules/auth';
import {OPS_OVERVIEW_ENABLED} from 'app/modules/operationalOverview/overviewFlags';
import {OPERATION_SETTINGS_FLAGS} from 'app/modules/operationSettings/operationSettingsFlag';

import {useMenuFlags} from './useMenuFlags';

export type AppMenuItem = {
  to: string;
  title: string;
  /** Classe do bootstrap-icons (um conjunto só, um ícone por item). */
  fontIcon: string;
  /** Recebe a contagem de "precisa da sua ação" dos Tickets. */
  ticketsBadge?: boolean;
  /**
   * Fora do menu lateral: a página só se acha pela paleta "Ir para…" (e o
   * breadcrumb a reconhece). Para as que não pedem um lugar fixo no menu.
   */
  asideHidden?: boolean;
};

/** Grupo que abre e fecha (Peças, Comercial). */
export type AppMenuGroup = {
  id: string;
  title: string;
  fontIcon: string;
  items: AppMenuItem[];
};

export type AppMenuEntry =
  | ({kind: 'item'} & AppMenuItem)
  | ({kind: 'group'} & AppMenuGroup);

/** Bloco do aside; sem título, só o espaço que separa. */
export type AppMenuSection = {
  id: string;
  title?: string;
  entries: AppMenuEntry[];
};

export type AppMenuContext = {
  role?: string;
  opsOverviewEnabled: boolean;
  /** As flags de produto lidas para a pessoa (`useMenuFlags`); sem, desligadas. */
  flags?: Record<string, boolean>;
};

const THIRD_PARTY_ROLE = 'ROLE_THIRD_PARTY_STOCK_MANAGER';

type Visible = (context: AppMenuContext) => boolean;
type ItemDef = AppMenuItem & {
  visible?: Visible;
  /**
   * Flag de produto: o item só aparece com ela ligada para a pessoa; com uma
   * lista, com qualquer uma delas.
   */
  flag?: string | readonly string[];
};
type EntryDef =
  | ({kind: 'item'} & ItemDef)
  | ({kind: 'group'} & Omit<AppMenuGroup, 'items'> & {items: ItemDef[]});
type SectionDef = {id: string; title?: string; entries: EntryDef[]};

const item = (
  to: string,
  title: string,
  fontIcon: string,
  extra: Partial<ItemDef> = {}
): ItemDef => ({to, title, fontIcon, ...extra});

const INCIDENT_DELIVERIES = item(
  '/incident-deliveries/tracking',
  'Logística reversa',
  'bi-arrow-counterclockwise'
);

const isAdmin: Visible = ({role}) => role === 'ROLE_MECANIZOU_ADMIN';

/**
 * O menu do gestor de estoque terceirizado: só a logística reversa, um item,
 * no topo, como sempre foi.
 */
const THIRD_PARTY_ITEMS: ItemDef[] = [INCIDENT_DELIVERIES];

// Na ordem do aside.
const MENU: SectionDef[] = [
  {
    id: 'inicio',
    entries: [
      {
        kind: 'item',
        ...item('/visao-operacional', 'Visão operacional', 'bi-grid-1x2', {
          visible: ({opsOverviewEnabled}) => opsOverviewEnabled,
        }),
      },
      {
        kind: 'item',
        ...item('/tickets', 'Tickets', 'bi-ticket-detailed', {
          ticketsBadge: true,
        }),
      },
    ],
  },
  {
    id: 'operacao',
    title: 'Operação',
    entries: [
      item('/quotes/workspace', 'Torre de controle', 'bi-broadcast'),
      item('/quotes/list', 'Cotações', 'bi-card-checklist'),
      item('/orders', 'Pedidos', 'bi-box-seam'),
      item('/purchases/tracking', 'Compras', 'bi-cart'),
      item('/deliveries/tracking-single-express', 'Entregas', 'bi-truck'),
      item(
        '/cancelled-items/list/stock-items',
        'Cancelamentos',
        'bi-x-octagon'
      ),
      INCIDENT_DELIVERIES,
      item('/operation-settings', 'Configurações', 'bi-sliders', {
        flag: OPERATION_SETTINGS_FLAGS,
      }),
    ].map((entry) => ({kind: 'item' as const, ...entry})),
  },
  {
    id: 'rede',
    title: 'Rede',
    entries: [
      item('/providers/list', 'Fornecedores', 'bi-buildings'),
      item('/mechanics/list', 'Oficinas', 'bi-shop'),
      item('/members/list', 'Mecanizados', 'bi-people'),
    ].map((entry) => ({kind: 'item' as const, ...entry})),
  },
  {
    id: 'mais',
    entries: [
      {
        kind: 'group',
        id: 'pecas',
        title: 'Peças',
        fontIcon: 'bi-nut',
        items: [
          item('/catalog/search', 'Pesquisa de peças', 'bi-search'),
          item(
            '/category-tree/nicknames',
            'Árvore de categorias',
            'bi-diagram-3'
          ),
        ],
      },
      {
        kind: 'group',
        id: 'comercial',
        title: 'Comercial',
        fontIcon: 'bi-megaphone',
        items: [
          item('/coupons/list', 'Cupons', 'bi-tag'),
          item('/banner-campaigns/campaigns', 'Banners', 'bi-image'),
          item('/dialer/campaigns', 'Discador', 'bi-telephone-outbound', {
            visible: isAdmin,
          }),
        ],
      },
      {kind: 'item', ...item('/finance/invoices', 'Financeiro', 'bi-wallet2')},
    ],
  },
  // O catálogo de padrões do Ops-Next: para o time de produto, então só na
  // paleta e só para o admin. Quem confere o papel é a página (`PadroesPage`
  // manda quem não é admin para o início), não a rota
  {
    id: 'catalogo',
    entries: [
      {
        kind: 'item',
        ...item('/padroes', 'Padrões Ops-Next', 'bi-palette', {
          visible: isAdmin,
          asideHidden: true,
        }),
      },
    ],
  },
];

export const isThirdPartyMenu = (role?: string) => role === THIRD_PARTY_ROLE;

/** Todas as definições, na ordem do menu (o item comum aos dois menus, duas vezes). */
const ALL_DEFS: ItemDef[] = [
  ...MENU.flatMap(({entries}) =>
    entries.flatMap<ItemDef>((entry) => {
      if (entry.kind === 'group') return entry.items;
      const {kind, ...rest} = entry;
      return [rest];
    })
  ),
  ...THIRD_PARTY_ITEMS,
];

/** As flags de produto que algum item confere (`useMenuFlags` as lê). */
export const MENU_FLAGS: string[] = Array.from(
  new Set(
    ALL_DEFS.flatMap(({flag}) => (flag ? ([] as string[]).concat(flag) : []))
  )
);

const shown = (context: AppMenuContext) => (entry: ItemDef) =>
  (!entry.visible || entry.visible(context)) &&
  (!entry.flag ||
    ([] as string[])
      .concat(entry.flag)
      .some((flag) => context.flags?.[flag] === true));

const toItem = ({visible, flag, ...entry}: ItemDef): AppMenuItem => entry;

/**
 * O que o aside mostra para esta pessoa: seções e grupos sem nenhum item
 * visível somem.
 */
export const visibleMenuTree = (context: AppMenuContext): AppMenuSection[] => {
  // O gestor de estoque terceirizado tem o menu próprio
  if (isThirdPartyMenu(context.role)) {
    return [
      {
        id: 'inicio',
        entries: THIRD_PARTY_ITEMS.map((entry) => ({
          kind: 'item' as const,
          ...toItem(entry),
        })),
      },
    ];
  }
  const visible = shown(context);
  return MENU.map(({id, title, entries}) => ({
    id,
    title,
    entries: entries.flatMap<AppMenuEntry>((entry) => {
      if (entry.kind === 'item') {
        const {kind, ...rest} = entry;
        return visible(rest) ? [{kind: 'item', ...toItem(rest)}] : [];
      }
      const items = entry.items.filter(visible).map(toItem);
      return items.length ? [{...entry, items}] : [];
    }),
  })).filter(({entries}) => entries.length > 0);
};

/**
 * O que o aside desenha de `visibleMenuTree`: sem os itens `asideHidden` e sem
 * o grupo ou a seção que ficariam vazios. A árvore completa segue sendo a
 * fonte da paleta e do breadcrumb.
 */
export const asideMenuTree = (tree: AppMenuSection[]): AppMenuSection[] =>
  tree
    .map((section) => ({
      ...section,
      entries: section.entries.flatMap<AppMenuEntry>((entry) => {
        if (entry.kind === 'item') return entry.asideHidden ? [] : [entry];
        const items = entry.items.filter(({asideHidden}) => !asideHidden);
        return items.length ? [{...entry, items}] : [];
      }),
    }))
    .filter(({entries}) => entries.length > 0);

/** As páginas do menu, na ordem do aside (para o breadcrumb e a paleta). */
export const flattenMenu = (tree: AppMenuSection[]): AppMenuItem[] =>
  tree.flatMap(({entries}) =>
    entries.flatMap((entry) => {
      if (entry.kind === 'group') return entry.items;
      const {kind, ...rest} = entry;
      return [rest];
    })
  );

export const visibleMenuItems = (context: AppMenuContext): AppMenuItem[] =>
  flattenMenu(visibleMenuTree(context));

/**
 * Todos os itens do menu, sem olhar papel nem flag (`visible`, `flag`): tudo o
 * que algum papel pode ver, uma vez cada. Para os testes que cobram o menu
 * inteiro (o mapa do roteiro em `ops-next/catalog/roteiro.test.ts`).
 */
export const allMenuItems = (): AppMenuItem[] =>
  ALL_DEFS.filter(
    ({to}, index) => ALL_DEFS.findIndex((i) => i.to === to) === index
  ).map(toItem);

export const useAppMenuTree = (): AppMenuSection[] => {
  const role = useAuth().currentUser?.role;
  const flags = useMenuFlags(MENU_FLAGS);
  return useMemo(
    () =>
      visibleMenuTree({role, opsOverviewEnabled: OPS_OVERVIEW_ENABLED, flags}),
    [role, flags]
  );
};

export const useAppMenu = (): AppMenuItem[] => {
  const tree = useAppMenuTree();
  return useMemo(() => flattenMenu(tree), [tree]);
};
